// frontend/src/pages/technician/TechnicianTickets.js

import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Alert,
    CircularProgress,
    Grid,
    TextField,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Paper,
    FormControl,
    InputLabel,
    Select,
    Tab,
    Tabs,
    Badge,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Build,
    Phone,
    Navigation,
    Message,
    CheckCircle,
    Schedule,
    Person,
    LocationOn,
    Refresh,
    FilterList,
    ExpandMore,
    Send,
    Close,
    Info,
    Warning,
    Error as ErrorIcon,
    SignalWifi4Bar,
    Router
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { technicianApiService } from '../../services/technicianApiService';

const TechnicianTickets = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [ticketDetails, setTicketDetails] = useState(null);
    const [detailDialog, setDetailDialog] = useState(false);
    const [responseDialog, setResponseDialog] = useState(false);
    const [resolveDialog, setResolveDialog] = useState(false);
    const [responseText, setResponseText] = useState('');
    const [resolutionText, setResolutionText] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        priority: '',
        category: ''
    });
    const [currentTab, setCurrentTab] = useState(0);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        loadTickets();
        loadStats();
    }, [filters]);

    const loadTickets = async () => {
        try {
            setLoading(true);
            const params = { limit: 50 };

            if (filters.status) params.status = filters.status;
            if (filters.priority) params.priority = filters.priority;
            if (filters.category) params.category = filters.category;

            const data = await technicianApiService.getAssignedTickets(params);
            setTickets(data.tickets);
            setError(null);
        } catch (error) {
            console.error('Error cargando tickets:', error);
            setError('Error cargando tickets de soporte');
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const statsData = await technicianApiService.getTicketStats();
            setStats(statsData);
        } catch (error) {
            console.error('Error cargando estadísticas:', error);
        }
    };

    const refreshData = async () => {
        try {
            setRefreshing(true);
            await loadTickets();
            await loadStats();
        } catch (error) {
            console.error('Error refrescando datos:', error);
        } finally {
            setRefreshing(false);
        }
    };

    const showTicketDetails = async (ticket) => {
        try {
            const details = await technicianApiService.getTicketDetails(ticket.id);
            setSelectedTicket(ticket);
            setTicketDetails(details);
            setDetailDialog(true);
        } catch (error) {
            console.error('Error cargando detalles:', error);
            setError('Error cargando detalles del ticket');
        }
    };

    const updateTicketStatus = async (ticketId, newStatus) => {
        try {
            await technicianApiService.updateTicketStatus(ticketId, newStatus);
            await refreshData();
            setDetailDialog(false);
        } catch (error) {
            console.error('Error actualizando estado:', error);
            setError('Error actualizando estado del ticket');
        }
    };

    const addResponse = async () => {
        if (!responseText.trim()) return;

        try {
            await technicianApiService.addTicketResponse(selectedTicket.id, {
                content: responseText,
                change_status: selectedTicket.status === 'open' ? 'in_progress' : null
            });

            setResponseText('');
            setResponseDialog(false);

            // Recargar detalles
            const details = await technicianApiService.getTicketDetails(selectedTicket.id);
            setTicketDetails(details);

            await refreshData();
        } catch (error) {
            console.error('Error agregando respuesta:', error);
            setError('Error agregando respuesta');
        }
    };

    const resolveTicket = async () => {
        if (!resolutionText.trim()) return;

        try {
            await technicianApiService.resolveTicket(selectedTicket.id, {
                resolution: resolutionText
            });

            setResolutionText('');
            setResolveDialog(false);
            setDetailDialog(false);

            await refreshData();
        } catch (error) {
            console.error('Error resolviendo ticket:', error);
            setError('Error resolviendo ticket');
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'urgent': return 'error';
            case 'high': return 'warning';
            case 'medium': return 'info';
            case 'low': return 'success';
            default: return 'default';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return 'error';
            case 'in_progress': return 'warning';
            case 'closed': return 'success';
            default: return 'default';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'open': return 'Abierto';
            case 'in_progress': return 'En Progreso';
            case 'closed': return 'Cerrado';
            default: return status;
        }
    };

    const getPriorityText = (priority) => {
        switch (priority) {
            case 'urgent': return 'Urgente';
            case 'high': return 'Alta';
            case 'medium': return 'Media';
            case 'low': return 'Baja';
            default: return priority;
        }
    };

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'urgent': return <ErrorIcon />;
            case 'high': return <Warning />;
            case 'medium': return <Info />;
            case 'low': return <CheckCircle />;
            default: return <Info />;
        }
    };

    const filterTicketsByTab = (tickets, tabIndex) => {
        switch (tabIndex) {
            case 0: return tickets; // Todos
            case 1: return tickets.filter(t => t.status === 'open');
            case 2: return tickets.filter(t => t.status === 'in_progress');
            case 3: return tickets.filter(t => t.status === 'closed');
            default: return tickets;
        }
    };

    const filteredTickets = filterTicketsByTab(tickets, currentTab);

    if (loading && tickets.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header con estadísticas */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5">
                        Tickets de Soporte Asignados
                    </Typography>
                    <IconButton onClick={refreshData} disabled={refreshing}>
                        <Refresh />
                    </IconButton>
                </Box>

                {stats && (
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                            <Card variant="outlined">
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography color="textSecondary" variant="caption">
                                        Abiertos
                                    </Typography>
                                    <Typography variant="h6" color="error">
                                        {stats.summary.open_tickets}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card variant="outlined">
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography color="textSecondary" variant="caption">
                                        En Progreso
                                    </Typography>
                                    <Typography variant="h6" color="warning.main">
                                        {stats.summary.in_progress_tickets}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card variant="outlined">
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography color="textSecondary" variant="caption">
                                        Resueltos
                                    </Typography>
                                    <Typography variant="h6" color="success.main">
                                        {stats.summary.closed_tickets}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card variant="outlined">
                                <CardContent sx={{ p: 1.5 }}>
                                    <Typography color="textSecondary" variant="caption">
                                        Tiempo Promedio
                                    </Typography>
                                    <Typography variant="h6">
                                        {Math.round(stats.summary.avg_resolution_hours || 0)}h
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Tabs de filtrado */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={currentTab}
                    onChange={(e, newValue) => setCurrentTab(newValue)}
                    variant="fullWidth"
                >
                    <Tab
                        label={
                            <Badge badgeContent={tickets.length} color="primary">
                                Todos
                            </Badge>
                        }
                    />
                    <Tab
                        label={
                            <Badge badgeContent={tickets.filter(t => t.status === 'open').length} color="error">
                                Abiertos
                            </Badge>
                        }
                    />
                    <Tab
                        label={
                            <Badge badgeContent={tickets.filter(t => t.status === 'in_progress').length} color="warning">
                                En Progreso
                            </Badge>
                        }
                    />
                    <Tab
                        label={
                            <Badge badgeContent={tickets.filter(t => t.status === 'closed').length} color="success">
                                Cerrados
                            </Badge>
                        }
                    />
                </Tabs>
            </Paper>

            {/* Lista de tickets */}
            {filteredTickets.length > 0 ? (
                <Grid container spacing={2}>
                    {filteredTickets.map((ticket) => (
                        <Grid item xs={12} md={6} key={ticket.id}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    {/* Header del ticket */}
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Avatar sx={{ bgcolor: getPriorityColor(ticket.priority) + '.main', width: 32, height: 32 }}>
                                                {getPriorityIcon(ticket.priority)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle1" noWrap>
                                                    {ticket.subject}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Ticket #{ticket.id.slice(0, 8)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Stack direction="row" spacing={1}>
                                            <Chip
                                                label={getPriorityText(ticket.priority)}
                                                color={getPriorityColor(ticket.priority)}
                                                size="small"
                                            />
                                            <Chip
                                                label={getStatusText(ticket.status)}
                                                color={getStatusColor(ticket.status)}
                                                size="small"
                                            />
                                        </Stack>
                                    </Box>

                                    {/* Información del cliente */}
                                    <Box mb={2}>
                                        <Typography variant="body2" gutterBottom>
                                            👤 {ticket.first_name} {ticket.last_name}
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            📍 {ticket.address}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            📅 {new Date(ticket.created_at).toLocaleDateString('es-PE')}
                                        </Typography>
                                    </Box>

                                    {/* Descripción del problema */}
                                    <Box mb={2}>
                                        <Typography variant="body2" color="textSecondary" sx={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden'
                                        }}>
                                            {ticket.description}
                                        </Typography>
                                    </Box>

                                    {/* Categoría y respuestas */}
                                    {ticket.category && (
                                        <Chip
                                            label={ticket.category}
                                            variant="outlined"
                                            size="small"
                                            sx={{ mb: 2 }}
                                        />
                                    )}

                                    {ticket.response_count > 0 && (
                                        <Box display="flex" alignItems="center" mb={2}>
                                            <Message sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="textSecondary">
                                                {ticket.response_count} respuesta{ticket.response_count !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Divider sx={{ my: 2 }} />

                                    {/* Acciones */}
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Box display="flex" gap={1}>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => technicianApiService.makePhoneCall(ticket.phone)}
                                                title="Llamar"
                                            >
                                                <Phone />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="success"
                                                onClick={() => {
                                                    if (ticket.coordinates) {
                                                        const coords = ticket.coordinates.replace(/[()]/g, '').split(',');
                                                        if (coords.length === 2) {
                                                            const val1 = parseFloat(coords[0].trim());
                                                            const val2 = parseFloat(coords[1].trim());
                                                            let lat = val1;
                                                            let lng = val2;
                                                            if (Math.abs(val1) > 90 && Math.abs(val2) <= 90) {
                                                                lat = val2;
                                                                lng = val1;
                                                            }
                                                            window.open(`https://maps.google.com/maps?q=${lat},${lng}`, '_blank');
                                                        }
                                                    }
                                                }}
                                                title="Navegar"
                                                disabled={!ticket.coordinates}
                                            >
                                                <Navigation />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="info"
                                                onClick={() => showTicketDetails(ticket)}
                                                title="Ver detalles"
                                            >
                                                <Info />
                                            </IconButton>
                                        </Box>

                                        {/* Botones de acción por estado */}
                                        <Box display="flex" gap={1}>
                                            {ticket.status === 'open' && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => updateTicketStatus(ticket.id, 'in_progress')}
                                                >
                                                    Tomar
                                                </Button>
                                            )}
                                            {ticket.status === 'in_progress' && (
                                                <>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => {
                                                            setSelectedTicket(ticket);
                                                            setResponseDialog(true);
                                                        }}
                                                    >
                                                        Responder
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="success"
                                                        onClick={() => {
                                                            setSelectedTicket(ticket);
                                                            setResolveDialog(true);
                                                        }}
                                                    >
                                                        Resolver
                                                    </Button>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Alert severity="info">
                    No se encontraron tickets {currentTab === 0 ? '' : getStatusText(['', 'open', 'in_progress', 'closed'][currentTab]).toLowerCase()}
                </Alert>
            )}

            {/* Dialog de detalles del ticket */}
            <Dialog
                open={detailDialog}
                onClose={() => setDetailDialog(false)}
                maxWidth="md"
                fullWidth
            >
                {ticketDetails && (
                    <>
                        <DialogTitle>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6">
                                    Ticket #{selectedTicket.id.slice(0, 8)}
                                </Typography>
                                <IconButton onClick={() => setDetailDialog(false)}>
                                    <Close />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent>
                            <Grid container spacing={3}>
                                {/* Información del ticket */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" gutterBottom>
                                        Información del Ticket
                                    </Typography>
                                    <Typography><strong>Asunto:</strong> {ticketDetails.ticket.subject}</Typography>
                                    <Typography><strong>Estado:</strong>
                                        <Chip
                                            label={getStatusText(ticketDetails.ticket.status)}
                                            color={getStatusColor(ticketDetails.ticket.status)}
                                            size="small"
                                            sx={{ ml: 1 }}
                                        />
                                    </Typography>
                                    <Typography><strong>Prioridad:</strong>
                                        <Chip
                                            label={getPriorityText(ticketDetails.ticket.priority)}
                                            color={getPriorityColor(ticketDetails.ticket.priority)}
                                            size="small"
                                            sx={{ ml: 1 }}
                                        />
                                    </Typography>
                                    <Typography><strong>Categoría:</strong> {ticketDetails.ticket.category}</Typography>
                                    <Typography><strong>Creado:</strong> {new Date(ticketDetails.ticket.created_at).toLocaleString('es-PE')}</Typography>

                                    <Box mt={2}>
                                        <Typography variant="subtitle1" gutterBottom>Descripción:</Typography>
                                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Typography variant="body2">
                                                {ticketDetails.ticket.description}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                </Grid>

                                {/* Información del cliente */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" gutterBottom>
                                        Información del Cliente
                                    </Typography>
                                    <Typography><strong>Nombre:</strong> {ticketDetails.ticket.first_name} {ticketDetails.ticket.last_name}</Typography>
                                    <Typography><strong>Teléfono:</strong> {ticketDetails.ticket.phone}</Typography>
                                    <Typography><strong>Email:</strong> {ticketDetails.ticket.email}</Typography>
                                    <Typography><strong>Dirección:</strong> {ticketDetails.ticket.address}</Typography>
                                    <Typography><strong>Plan:</strong> {ticketDetails.ticket.plan_name}</Typography>
                                    <Typography><strong>IP:</strong> {ticketDetails.ticket.ip_address}</Typography>

                                    {/* Información de conectividad */}
                                    {ticketDetails.connectivity_info && ticketDetails.connectivity_info.length > 0 && (
                                        <Box mt={2}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Estado de Conexión
                                            </Typography>
                                            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <SignalWifi4Bar color="primary" />
                                                    <Typography variant="body2">
                                                        Última conexión: {new Date(ticketDetails.connectivity_info[0].timestamp).toLocaleString('es-PE')}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        </Box>
                                    )}
                                </Grid>

                                {/* Historial de respuestas */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>
                                        Historial de Conversación ({ticketDetails.responses.length})
                                    </Typography>
                                    <Box maxHeight={300} overflow="auto">
                                        {ticketDetails.responses.map((response, index) => (
                                            <Box key={response.id} mb={2}>
                                                <Paper sx={{ p: 2, bgcolor: response.response_type === 'client' ? 'blue.50' : 'green.50' }}>
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                        <Typography variant="subtitle2">
                                                            {response.response_by_name} ({response.response_by_type})
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {new Date(response.created_at).toLocaleString('es-PE')}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                        {response.content}
                                                    </Typography>
                                                </Paper>
                                            </Box>
                                        ))}
                                    </Box>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            {ticketDetails.ticket.status !== 'closed' && (
                                <>
                                    <Button
                                        onClick={() => {
                                            setResponseDialog(true);
                                            setDetailDialog(false);
                                        }}
                                        startIcon={<Message />}
                                    >
                                        Responder
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setResolveDialog(true);
                                            setDetailDialog(false);
                                        }}
                                        variant="contained"
                                        color="success"
                                        startIcon={<CheckCircle />}
                                    >
                                        Resolver
                                    </Button>
                                </>
                            )}
                            <Button onClick={() => setDetailDialog(false)}>
                                Cerrar
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Dialog para agregar respuesta */}
            <Dialog open={responseDialog} onClose={() => setResponseDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Agregar Respuesta</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        multiline
                        rows={4}
                        fullWidth
                        label="Tu respuesta"
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        placeholder="Describe las acciones realizadas o la información proporcionada al cliente..."
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResponseDialog(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={addResponse}
                        variant="contained"
                        disabled={!responseText.trim()}
                        startIcon={<Send />}
                    >
                        Enviar Respuesta
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog para resolver ticket */}
            <Dialog open={resolveDialog} onClose={() => setResolveDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Resolver Ticket</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        multiline
                        rows={4}
                        fullWidth
                        label="Descripción de la resolución"
                        value={resolutionText}
                        onChange={(e) => setResolutionText(e.target.value)}
                        placeholder="Describe cómo se resolvió el problema y las acciones tomadas..."
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResolveDialog(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={resolveTicket}
                        variant="contained"
                        color="success"
                        disabled={!resolutionText.trim()}
                        startIcon={<CheckCircle />}
                    >
                        Resolver Ticket
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TechnicianTickets;